import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class H3Boundary extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.h3boundary',
        group: 'Spatial Indexes',
        title: 'H3 Boundary',
        description: `
    This component takes a table with H3 indices and generates a new one with an
    additional column containing the boundaries of those H3 cells.
    `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'h3col',
          type: WorkflowComponentParamType.Column,
          title: 'H3 column',
          parent: 'source',
          placeholder: 'h3',
          dataType: [ColumnDataType.String]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let select = ''
    if (connection.type === Provider.DatabricksRest) {
      select = dedent`SELECT /*+ REPARTITION(40, ${preparedParams.h3col}_geo) */
                    H3_BOUNDARYASWKB(
                        ${preparedParams.h3col}
                    ) ${preparedParams.h3col}_geo, *
                  FROM ${connection.quoteName(preparedParams.source as string)}`
    } else {
      select = dedent`SELECT
                    ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_BOUNDARY(
                        ${preparedParams.h3col}
                    ) ${preparedParams.h3col}_geo, *
                  FROM ${connection.quoteName(preparedParams.source as string)}`
    }
    let query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    if (connection.type === Provider.Postgres) {
      query = dedent`IF EXISTS(SELECT 1 FROM pg_extension WHERE extname = 'plv8') THEN
                          ${dedent(query)}
                        ELSE
                          RAISE EXCEPTION 'plv8 extension not installed';
                        END IF;`
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.1.1'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.h3col}_geo`,
      type: connection.getGeoDataType()
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.DatabricksRest]
  }
}

export { H3Boundary }
